import { clusterConvoyByProximity } from '../cluster-by-proximity';
import type { ConvoyMemberInput } from '../types';
import { estimatePositionAt } from './position-at';
import { buildEventTimeline } from './timeline';
import type {
  DeviceTrackPoints,
  MembershipSnapshot,
} from './types';

function partitionKey(groups: Map<string, number[]>): string {
  const keys = [...groups.keys()].sort();
  return keys
    .map((k) => `${k}:${(groups.get(k) ?? []).slice().sort((a, b) => a - b).join(',')}`)
    .join('|');
}

export function snapshotFromPoses(
  poses: ConvoyMemberInput[],
  timestampSec: number,
  radiusMeters: number,
): MembershipSnapshot {
  const clusters = clusterConvoyByProximity(poses, { radiusMeters });
  const groups = new Map<string, number[]>();
  for (const c of clusters) {
    groups.set(c.id, [...c.memberDeviceIds]);
  }
  return { timestampSec, groups };
}

/**
 * Build raw membership snapshots at every event time (no hysteresis yet).
 */
export function buildRawMembershipSeries(
  tracks: readonly DeviceTrackPoints[],
  options: {
    radiusMeters: number;
    holdMaxSec: number;
  },
): MembershipSnapshot[] {
  const times = buildEventTimeline(tracks);
  const out: MembershipSnapshot[] = [];
  for (const t of times) {
    const poses: ConvoyMemberInput[] = [];
    for (const track of tracks) {
      const pose = estimatePositionAt(track, t, options);
      if (!pose) continue;
      poses.push({
        deviceId: pose.deviceId,
        lat: pose.lat,
        lng: pose.lng,
        timestampSec: pose.timestampSec,
      });
    }
    if (poses.length === 0) continue;
    out.push(snapshotFromPoses(poses, t, options.radiusMeters));
  }
  return out;
}

function cloneSnapshot(
  source: MembershipSnapshot,
  timestampSec: number,
): MembershipSnapshot {
  return {
    timestampSec,
    groups: new Map(source.groups),
  };
}

/**
 * Adopt a new partition only after it holds for membershipMinSec.
 * Once confirmed, the fork is backdated to when the new partition first appeared
 * so spur geometry starts at the real split, not at t_commit.
 */
export function applyMembershipHysteresis(
  raw: readonly MembershipSnapshot[],
  membershipMinSec: number,
): MembershipSnapshot[] {
  if (raw.length === 0) return [];

  const out: MembershipSnapshot[] = [];
  let committed = raw[0]!;
  let pending: MembershipSnapshot | null = null;
  let pendingSince = committed.timestampSec;

  out.push(cloneSnapshot(committed, raw[0]!.timestampSec));

  for (let i = 1; i < raw.length; i++) {
    const snap = raw[i]!;
    const key = partitionKey(snap.groups);
    const committedKey = partitionKey(committed.groups);

    if (key === committedKey) {
      pending = null;
      out.push(cloneSnapshot(committed, snap.timestampSec));
      continue;
    }

    if (!pending || partitionKey(pending.groups) !== key) {
      pending = snap;
      pendingSince = snap.timestampSec;
    }

    const held = snap.timestampSec - pendingSince;
    if (held >= membershipMinSec) {
      committed = pending;
      pending = null;
      for (const o of out) {
        if (o.timestampSec >= pendingSince) {
          o.groups = new Map(committed.groups);
        }
      }
      out.push(cloneSnapshot(committed, snap.timestampSec));
    } else {
      out.push(cloneSnapshot(committed, snap.timestampSec));
    }
  }

  return out;
}

export function buildMembershipSeries(
  tracks: readonly DeviceTrackPoints[],
  options: {
    radiusMeters: number;
    holdMaxSec: number;
    membershipMinSec: number;
  },
): MembershipSnapshot[] {
  const raw = buildRawMembershipSeries(tracks, options);
  return applyMembershipHysteresis(raw, options.membershipMinSec);
}
